import React, { useState, useEffect, useMemo } from 'react';
import { 
  Users, 
  Search, 
  Filter, 
  Plus, 
  MoreVertical, 
  MessageSquare, 
  Mail, 
  Twitter, 
  Linkedin, 
  Globe, 
  Send, 
  Sparkles, 
  Clock, 
  CheckCircle2, 
  AlertCircle, 
  ArrowLeft,
  ChevronRight,
  Edit2,
  Save,
  X,
  Trash2,
  UserPlus,
  Loader2 as LucideLoader2
} from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { db, auth, collection, query, where, onSnapshot, addDoc, updateDoc, doc, serverTimestamp, deleteDoc } from '../firebase';
import { useSearchParams, Link, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { GoogleGenAI } from "@google/genai";
import { getGeminiKey, callGeminiWithRetry } from '../lib/gemini';
import { createCheckoutSession } from '../lib/stripe';

enum OperationType {
  CREATE = 'create',
  UPDATE = 'update',
  DELETE = 'delete',
  LIST = 'list',
  GET = 'get',
  WRITE = 'write',
}

function handleFirestoreError(error: unknown, operationType: OperationType, path: string | null) {
  const errInfo = {
    error: error instanceof Error ? error.message : String(error),
    authInfo: {
      userId: auth.currentUser?.uid,
      email: auth.currentUser?.email,
      emailVerified: auth.currentUser?.emailVerified,
      isAnonymous: auth.currentUser?.isAnonymous,
      tenantId: auth.currentUser?.tenantId,
      providerInfo: auth.currentUser?.providerData.map(provider => ({
        providerId: provider.providerId,
        displayName: provider.displayName,
        email: provider.email,
        photoUrl: provider.photoURL
      })) || []
    },
    operationType,
    path
  };
  console.error('Firestore Error: ', JSON.stringify(errInfo));
  throw new Error(JSON.stringify(errInfo));
}

interface Lead {
  id: string;
  name: string;
  platform: 'X' | 'Reddit' | 'LinkedIn' | 'Email';
  handle: string;
  status: 'New' | 'Contacted' | 'Responded' | 'Converted' | 'Lost';
  details: string;
  contactInfo: string;
  notes: string;
  role?: string;
  company?: string;
  context?: string;
  socialLinks?: {
    x?: string;
    linkedin?: string;
    reddit?: string;
    website?: string;
  };
  lastContacted?: any;
  personalizedMessage?: string;
  projectId: string;
  createdAt: any;
}

export default function OutreachTracker({ profile }: { profile: any }) {
  const [searchParams] = useSearchParams();
  const projectId = searchParams.get('project');
  const navigate = useNavigate();
  
  const [leads, setLeads] = useState<Lead[]>([]);
  const [selectedLeadId, setSelectedLeadId] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [isGenerating, setIsGenerating] = useState(false);
  const [isAddingLead, setIsAddingLead] = useState(false);
  const [isEditingLead, setIsEditingLead] = useState(false);
  const [selectedLeadIds, setSelectedLeadIds] = useState<Set<string>>(new Set());
  const [isSending, setIsSending] = useState(false);

  const handleUpgrade = async () => {
    if (!auth.currentUser) {
      toast.error("Authentication required", {
        description: "Please sign in to upgrade your plan."
      });
      return;
    }

    const toastId = toast.loading("Preparing checkout...");
    try {
      await createCheckoutSession(auth.currentUser.uid, 'pro');
    } catch (error: any) {
      console.error("Upgrade error:", error);
      toast.error("Checkout Error", {
        description: error.message || "Failed to initiate checkout. Please try again.",
        id: toastId
      });
    }
  };

  // New Lead Form State
  const [newLead, setNewLead] = useState<Partial<Lead>>({
    name: '',
    platform: 'X',
    handle: '',
    status: 'New',
    details: '',
    contactInfo: '',
    notes: ''
  });

  const selectedLead = useMemo(() => 
    leads.find(l => l.id === selectedLeadId), 
    [leads, selectedLeadId]
  );

  useEffect(() => {
    const leadId = searchParams.get('lead');
    if (leadId) {
      setSelectedLeadId(leadId);
    }
  }, [searchParams]);

  useEffect(() => {
    if (!projectId || !auth.currentUser) return;

    const q = query(
      collection(db, 'outreach'),
      where('projectId', '==', projectId)
    );

    const unsubscribe = onSnapshot(q, (snapshot) => {
      const leadsData = snapshot.docs.map(doc => ({ 
        id: doc.id, 
        ...doc.data() 
      })) as Lead[];

      setLeads(leadsData);
      
      // Only set default if no lead is currently selected
      const urlLeadId = new URLSearchParams(window.location.search).get('lead');
      if (leadsData.length > 0 && !selectedLeadId && !urlLeadId) {
        setSelectedLeadId(leadsData[0].id);
      } else if (urlLeadId && !selectedLeadId) {
        setSelectedLeadId(urlLeadId);
      }
      setLoading(false);
    }, (error) => {
      handleFirestoreError(error, OperationType.GET, 'outreach');
    });

    return unsubscribe;
  }, [projectId, auth.currentUser, selectedLeadId]);

  const toggleSelectLead = (id: string, e: React.MouseEvent) => {
    e.stopPropagation();
    setSelectedLeadIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const handleBulkDeleteLeads = async () => {
    if (selectedLeadIds.size === 0) return;
    const confirmDelete = window.confirm(`Are you sure you want to delete ${selectedLeadIds.size} leads?`);
    if (!confirmDelete) return;

    const toastId = toast.loading(`Deleting ${selectedLeadIds.size} leads...`);
    try {
      for (const id of selectedLeadIds) {
        await deleteDoc(doc(db, 'outreach', id));
      }
      setSelectedLeadIds(new Set());
      toast.success("Leads deleted successfully", { id: toastId });
    } catch (error) {
      toast.error("Failed to delete some leads", { id: toastId });
    }
  };

  const handleBulkUpdateStatus = async (status: Lead['status']) => {
    if (selectedLeadIds.size === 0) return;
    const toastId = toast.loading(`Updating ${selectedLeadIds.size} leads...`);
    try {
      for (const id of selectedLeadIds) {
        await updateDoc(doc(db, 'outreach', id), { status });
      }
      setSelectedLeadIds(new Set());
      toast.success("Leads updated successfully", { id: toastId });
    } catch (error) {
      toast.error("Failed to update some leads", { id: toastId });
    }
  };

  useEffect(() => {
    if (selectedLead && !selectedLead.personalizedMessage && !isGenerating) {
      generatePersonalizedMessage();
    }
  }, [selectedLeadId]);

  const handleAddLead = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!projectId || !auth.currentUser || !newLead.name || !newLead.handle) return;

    // Check for uniqueness
    const isDuplicate = leads.some(l => 
      (l.handle?.toLowerCase() || "") === newLead.handle?.toLowerCase() && 
      l.platform === newLead.platform
    );

    if (isDuplicate) {
      toast.error('Duplicate Lead', {
        description: `A lead with handle ${newLead.handle} already exists on ${newLead.platform}.`
      });
      return;
    }

    // Check plan limits
    const limit = profile?.isPro ? 10 : 3;
    if (leads.length >= limit) {
      toast.error('Lead Limit Reached', {
        description: `Your ${profile?.isPro ? 'Pro' : 'Free'} plan is limited to ${limit} leads.`,
        action: !profile?.isPro ? {
          label: "Upgrade",
          onClick: handleUpgrade
        } : undefined
      });
      return;
    }

    try {
      const docRef = await addDoc(collection(db, 'outreach'), {
        ...newLead,
        projectId,
        createdAt: serverTimestamp(),
        status: 'New'
      });
      setIsAddingLead(false);
      setNewLead({
        name: '',
        platform: 'X',
        handle: '',
        status: 'New',
        details: '',
        contactInfo: '',
        notes: '',
        role: '',
        company: '',
        context: '',
        socialLinks: {
          x: '',
          linkedin: '',
          reddit: '',
          website: ''
        }
      });
      setSelectedLeadId(docRef.id);
      toast.success('Lead added successfully.');
    } catch (error) {
      handleFirestoreError(error, OperationType.CREATE, 'outreach');
    }
  };

  const handleUpdateLead = async (updates: Partial<Lead>) => {
    if (!selectedLeadId) return;
    try {
      await updateDoc(doc(db, 'outreach', selectedLeadId), updates);
      toast.success('Lead updated.');
    } catch (error) {
      handleFirestoreError(error, OperationType.UPDATE, `outreach/${selectedLeadId}`);
    }
  };

  const handleDeleteLead = async (id: string) => {
    try {
      await deleteDoc(doc(db, 'outreach', id));
      if (selectedLeadId === id) setSelectedLeadId(null);
      toast.success('Lead deleted.');
    } catch (error) {
      handleFirestoreError(error, OperationType.DELETE, `outreach/${id}`);
    }
  };

  const generatePersonalizedMessage = async () => {
    if (!selectedLead) return;
    setIsGenerating(true);

    try {
      const ai = new GoogleGenAI({ apiKey: getGeminiKey() });
      const model = "gemini-3-flash-preview";
      
      const prompt = `
        Generate a highly personalized outreach message for the following lead:
        Name: ${selectedLead.name}
        Platform: ${selectedLead.platform}
        Handle: ${selectedLead.handle}
        Details: ${selectedLead.details}
        Notes: ${selectedLead.notes}

        The product is "Yacht Labs", an AI-powered SaaS synthesis and deployment engine.
        Tone: Professional, helpful, concise, and slightly provocative (brutalist/modern vibe).
        Platform specific: If X, keep it short (DM style). If LinkedIn, more professional.
        
        Return ONLY the message text.
      `;

      const response = await callGeminiWithRetry(() => ai.models.generateContent({
        model,
        contents: [{ parts: [{ text: prompt }] }]
      }));

      const message = response.text || "Failed to generate message.";
      await handleUpdateLead({ personalizedMessage: message });
      toast.success('Personalized message generated.');
    } catch (error) {
      console.error('Error generating message:', error);
      toast.error('Failed to generate message.');
    } finally {
      setIsGenerating(false);
    }
  };

  const filteredLeads = useMemo(() => {
    return leads
      .filter(l => {
        const matchesSearch = (l.name?.toLowerCase() || "").includes(searchTerm.toLowerCase()) || 
                            (l.handle?.toLowerCase() || "").includes(searchTerm.toLowerCase());
        const matchesStatus = statusFilter === 'all' || l.status === statusFilter;
        return matchesSearch && matchesStatus;
      })
      .sort((a, b) => {
        // Sort by created at desc
        const timeA = a.createdAt?.seconds || 0;
        const timeB = b.createdAt?.seconds || 0;
        return timeB - timeA;
      });
  }, [leads, searchTerm, statusFilter]);

  if (loading) {
    return (
      <div className="h-screen flex items-center justify-center bg-[#050505]">
        <motion.div 
          animate={{ rotate: 360 }}
          transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
          className="w-8 h-8 border-2 border-[#222222] border-t-white rounded-full"
        />
      </div>
    );
  }

  return (
    <div className="h-screen flex flex-col bg-[#050505] text-white overflow-hidden">
      {/* Header */}
      <header className="tech-header z-10">
        <div className="flex items-center gap-4">
          <Link 
            to={`/studio/listen?project=${projectId}`}
            className="w-10 h-10 bg-[#111111] border border-[#222222] rounded-lg flex items-center justify-center hover:bg-[#1A1A1A] transition-colors"
          >
            <ArrowLeft size={18} />
          </Link>
          <div>
            <h1 className="tech-label text-white">Outreach Tracker</h1>
            <p className="text-[10px] text-[#888888] font-sans italic">Managing {leads.length} leads across the global edge.</p>
          </div>
        </div>
        <div className="flex items-center gap-4">
          <button 
            onClick={() => {
              const industry = prompt("Enter industry or niche for lead generation:", "SaaS Founders");
              if (industry) {
                navigate(`/studio/inference?project=${projectId}&action=generate_leads&industry=${encodeURIComponent(industry)}`);
              }
            }}
            className="flex items-center gap-2 px-4 py-2 bg-[#111111] border border-[#222222] text-white rounded font-mono uppercase text-xs tracking-widest hover:bg-[#1A1A1A] transition-all"
          >
            <Sparkles size={16} className="text-[#0074D9]" /> Generate via Think
          </button>
          <button 
            onClick={() => setIsAddingLead(true)}
            className="tech-button px-4 py-2 flex items-center gap-2"
          >
            <UserPlus size={16} /> Add Lead
          </button>
        </div>
      </header>

      <div className="flex-1 flex overflow-hidden">
        {/* Left Panel: Lead List */}
        <div className="w-96 border-r border-[#222222] flex flex-col bg-[#050505]">
          <div className="p-4 border-b border-[#222222] space-y-4">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-[#555555]" size={16} />
              <input 
                type="text" 
                placeholder="Search leads..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="tech-input w-full pl-10"
              />
            </div>
            <div className="flex items-center gap-2 overflow-x-auto pb-2 no-scrollbar">
              {['all', 'New', 'Contacted', 'Responded', 'Converted', 'Lost'].map(status => (
                <button
                  key={status}
                  onClick={() => setStatusFilter(status)}
                  className={`px-3 py-1 rounded text-[10px] font-mono uppercase tracking-widest whitespace-nowrap transition-all ${
                    statusFilter === status 
                      ? 'bg-[#0074D9] text-white' 
                      : 'bg-[#111111] text-[#555555] border border-[#222222] hover:border-[#333333]'
                  }`}
                >
                  {status}
                </button>
              ))}
            </div>
          </div>

          <div className="flex-1 overflow-y-auto p-2 space-y-1">
            {filteredLeads.map(lead => (
              <div
                key={lead.id}
                onClick={() => setSelectedLeadId(lead.id)}
                className={`w-full text-left p-4 rounded transition-all group relative flex items-start gap-3 cursor-pointer ${
                  selectedLeadId === lead.id 
                    ? 'bg-[#111111] border border-[#222222]' 
                    : 'hover:bg-[#111111]/50 border border-transparent'
                }`}
              >
                <div 
                  onClick={(e) => toggleSelectLead(lead.id, e)}
                  className={`mt-1 w-4 h-4 rounded border flex items-center justify-center transition-all shrink-0 ${
                    selectedLeadIds.has(lead.id) 
                      ? 'bg-[#0074D9] border-[#0074D9]' 
                      : 'border-[#333333] group-hover:border-[#555555]'
                  }`}
                >
                  {selectedLeadIds.has(lead.id) && <CheckCircle2 size={10} className="text-white" />}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-2">
                      <div className={`w-6 h-6 rounded flex items-center justify-center ${
                        lead.platform === 'X' ? 'bg-[#111111] text-white' :
                        lead.platform === 'Reddit' ? 'bg-[#FF4136]/10 text-[#FF4136]' :
                        lead.platform === 'LinkedIn' ? 'bg-[#0074D9]/10 text-[#0074D9]' :
                        'bg-[#111111] text-[#888888]'
                      }`}>
                        {lead.platform === 'X' ? <Twitter size={12} /> :
                         lead.platform === 'Reddit' ? <Globe size={12} /> :
                         lead.platform === 'LinkedIn' ? <Linkedin size={12} /> :
                         <Mail size={12} />}
                      </div>
                      <span className="text-[10px] font-mono text-[#555555] uppercase tracking-widest">{lead.platform}</span>
                    </div>
                    <span className={`text-[8px] font-mono uppercase px-1.5 py-0.5 rounded border ${
                      lead.status === 'New' ? 'border-[#0074D9]/30 text-[#0074D9] bg-[#0074D9]/10' :
                      lead.status === 'Contacted' ? 'border-[#FF851B]/30 text-[#FF851B] bg-[#FF851B]/10' :
                      lead.status === 'Responded' ? 'border-[#2ECC40]/30 text-[#2ECC40] bg-[#2ECC40]/10' :
                      lead.status === 'Converted' ? 'border-[#01FF70]/30 text-[#01FF70] bg-[#01FF70]/10' :
                      'border-[#222222] text-[#888888] bg-[#111111]/50'
                    }`}>
                      {lead.status}
                    </span>
                  </div>
                  <h3 className="text-sm font-medium text-white mb-1 truncate">{lead.name}</h3>
                  <p className="text-[10px] text-[#555555] font-mono truncate">{lead.handle}</p>
                </div>
                
                <button 
                  onClick={(e) => {
                    e.stopPropagation();
                    handleDeleteLead(lead.id);
                  }}
                  className="absolute top-4 right-4 opacity-0 group-hover:opacity-100 p-1 hover:text-[#FF4444] transition-all"
                >
                  <Trash2 size={14} />
                </button>
              </div>
            ))}
            {filteredLeads.length === 0 && (
              <div className="p-8 text-center">
                <p className="text-xs text-[#555555] font-mono uppercase tracking-widest">No leads found.</p>
              </div>
            )}
          </div>
        </div>

        {/* Right Panel: Lead Details */}
        <div className="flex-1 overflow-y-auto bg-[#050505]">
          {selectedLead ? (
            <div className="max-w-4xl mx-auto p-12">
              <div className="flex items-start justify-between mb-12">
                <div className="flex items-center gap-6">
                  <div className="w-20 h-20 bg-[#111111] border border-[#222222] rounded flex items-center justify-center text-3xl font-mono text-[#555555]">
                    {selectedLead.name?.[0] || '?'}
                  </div>
                  <div>
                    <div className="flex items-center gap-3 mb-2">
                      <h2 className="text-3xl font-mono uppercase tracking-tighter text-white">{selectedLead.name}</h2>
                      <button 
                        onClick={() => setIsEditingLead(!isEditingLead)}
                        className="p-2 hover:bg-[#111111] rounded transition-colors text-[#555555] hover:text-white"
                      >
                        <Edit2 size={16} />
                      </button>
                    </div>
                    <div className="flex flex-wrap items-center gap-4 text-sm text-[#888888]">
                      <span className="flex items-center gap-1.5"><Globe size={14} /> {selectedLead.platform}</span>
                      <span className="flex items-center gap-1.5"><MessageSquare size={14} /> {selectedLead.handle}</span>
                      {selectedLead.role && <span className="flex items-center gap-1.5"><Users size={14} /> {selectedLead.role}</span>}
                      {selectedLead.company && <span className="flex items-center gap-1.5"><Globe size={14} /> {selectedLead.company}</span>}
                      <span className="flex items-center gap-1.5"><Clock size={14} /> Added {new Date(selectedLead.createdAt?.seconds * 1000).toLocaleDateString()}</span>
                    </div>
                    
                    <div className="flex items-center gap-3 mt-4">
                      {selectedLead.socialLinks?.x && (
                        <a href={selectedLead.socialLinks.x} target="_blank" rel="noopener noreferrer" className="p-2 bg-[#111111] border border-[#222222] rounded text-[#555555] hover:text-white transition-all">
                          <Twitter size={14} />
                        </a>
                      )}
                      {selectedLead.socialLinks?.linkedin && (
                        <a href={selectedLead.socialLinks.linkedin} target="_blank" rel="noopener noreferrer" className="p-2 bg-[#111111] border border-[#222222] rounded text-[#555555] hover:text-white transition-all">
                          <Linkedin size={14} />
                        </a>
                      )}
                      {selectedLead.socialLinks?.reddit && (
                        <a href={selectedLead.socialLinks.reddit} target="_blank" rel="noopener noreferrer" className="p-2 bg-[#111111] border border-[#222222] rounded text-[#555555] hover:text-white transition-all">
                          <Globe size={14} />
                        </a>
                      )}
                      {selectedLead.socialLinks?.website && (
                        <a href={selectedLead.socialLinks.website} target="_blank" rel="noopener noreferrer" className="p-2 bg-[#111111] border border-[#222222] rounded text-[#555555] hover:text-white transition-all">
                          <Globe size={14} />
                        </a>
                      )}
                    </div>
                  </div>
                </div>
                <div className="flex flex-col items-end gap-2">
                  <select 
                    value={selectedLead.status}
                    onChange={(e) => handleUpdateLead({ status: e.target.value as any })}
                    className="tech-input"
                  >
                    {['New', 'Contacted', 'Responded', 'Converted', 'Lost'].map(s => (
                      <option key={s} value={s}>{s}</option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                <div className="lg:col-span-2 space-y-8">
                  {/* Message Section */}
                  <section className="tech-panel overflow-hidden">
                    <div className="p-6 border-b border-[#222222] flex items-center justify-between bg-[#111111]/50">
                      <h3 className="text-xs font-mono uppercase tracking-widest text-white flex items-center gap-2">
                        <Sparkles size={14} className="text-[#0074D9]" /> Personalized Message
                      </h3>
                      <button 
                        onClick={generatePersonalizedMessage}
                        disabled={isGenerating}
                        className="flex items-center gap-2 px-3 py-1.5 bg-[#0074D9] text-white rounded text-[10px] font-mono uppercase tracking-widest hover:bg-[#0056b3] transition-all disabled:opacity-50"
                      >
                        {isGenerating ? <Loader2 className="animate-spin" size={12} /> : <Sparkles size={12} />}
                        {selectedLead.personalizedMessage ? 'Regenerate' : 'Generate'}
                      </button>
                    </div>
                    <div className="p-8">
                      {selectedLead.personalizedMessage ? (
                        <div className="space-y-6">
                          <div className="p-6 bg-[#050505] border border-[#222222] rounded italic text-[#888888] leading-relaxed">
                            "{selectedLead.personalizedMessage}"
                          </div>
                          <div className="flex justify-end gap-4">
                            <button 
                              onClick={() => {
                                navigator.clipboard.writeText(selectedLead.personalizedMessage || '');
                                toast.success('Copied to clipboard.');
                              }}
                              className="text-[10px] font-mono uppercase tracking-widest text-[#555555] hover:text-white transition-colors"
                            >
                              Copy Text
                            </button>
                            <button 
                              disabled={isSending}
                              onClick={async () => {
                                const platform = selectedLead.platform;
                                const handle = selectedLead.handle;
                                const message = selectedLead.personalizedMessage;
                                
                                setIsSending(true);
                                
                                // Agentic simulation
                                toast.promise(
                                  new Promise((resolve) => setTimeout(resolve, 2000)),
                                  {
                                    loading: `Agentic ${platform} Engine Initializing...`,
                                    success: () => {
                                      setIsSending(false);
                                      // Copy to clipboard
                                      navigator.clipboard.writeText(message || '');
                                      
                                      // Open platform
                                      let url = '';
                                      if (platform === 'X') {
                                        url = `https://x.com/${handle.replace('@', '')}`;
                                      } else if (platform === 'LinkedIn') {
                                        url = handle.startsWith('http') ? handle : `https://www.linkedin.com/in/${handle}`;
                                      } else if (platform === 'Reddit') {
                                        url = `https://www.reddit.com/u/${handle.replace('u/', '')}`;
                                      } else if (platform === 'Email') {
                                        url = `mailto:${handle}?subject=Yacht%20Labs%20Inquiry&body=${encodeURIComponent(message || '')}`;
                                      }
                                      
                                      if (url) window.open(url, '_blank');
                                      
                                      // Update status
                                      handleUpdateLead({ 
                                        status: 'Contacted', 
                                        lastContacted: serverTimestamp() 
                                      });
                                      
                                      return `Message copied & ${platform} profile opened.`;
                                    },
                                    error: 'Agentic Engine failed to initialize.',
                                  }
                                );
                              }}
                              className="tech-button flex items-center gap-2 px-6 py-3"
                            >
                              {isSending ? <LucideLoader2 className="animate-spin" size={14} /> : <Send size={14} />} 
                              {isSending ? 'Sending Agentically...' : `Send via ${selectedLead.platform}`}
                            </button>
                          </div>
                        </div>
                      ) : (
                        <div className="text-center py-12">
                          <Sparkles size={32} className="text-[#333333] mx-auto mb-4" />
                          <p className="text-xs text-[#555555] font-mono uppercase tracking-widest">No message generated yet.</p>
                        </div>
                      )}
                    </div>
                  </section>

                  {/* Details Section */}
                  <section className="space-y-4">
                    <h3 className="text-xs font-mono uppercase tracking-widest text-[#555555]">Lead Context</h3>
                    <div className="tech-panel p-6 space-y-4">
                      {selectedLead.context && (
                        <div className="pb-4 border-b border-[#222222]">
                          <p className="text-[10px] font-mono uppercase text-[#555555] mb-1">Context</p>
                          <p className="text-sm text-[#888888] leading-relaxed italic">{selectedLead.context}</p>
                        </div>
                      )}
                      <div>
                        <p className="text-[10px] font-mono uppercase text-[#555555] mb-1">Details</p>
                        <p className="text-sm text-[#888888] leading-relaxed italic">
                          {selectedLead.details || 'No details provided.'}
                        </p>
                      </div>
                    </div>
                  </section>
                </div>

                <div className="space-y-8">
                  {/* Notes Section */}
                  <section className="tech-panel p-6">
                    <h3 className="text-xs font-mono uppercase tracking-widest text-white mb-4 flex items-center gap-2">
                      <Edit2 size={14} className="text-[#555555]" /> Internal Notes
                    </h3>
                    <textarea 
                      value={selectedLead.notes}
                      onChange={(e) => handleUpdateLead({ notes: e.target.value })}
                      placeholder="Add notes about this lead..."
                      className="tech-input w-full h-40 resize-none mb-4"
                    />
                    <div className="flex items-center justify-between">
                      <span className="text-[8px] font-mono text-[#555555] uppercase">Auto-saving...</span>
                      <button className="text-[10px] font-mono uppercase text-[#555555] hover:text-white transition-colors">
                        Clear Notes
                      </button>
                    </div>
                  </section>

                  {/* Contact Info */}
                  <section className="tech-panel p-6">
                    <h3 className="text-xs font-mono uppercase tracking-widest text-white mb-4 flex items-center gap-2">
                      <AlertCircle size={14} className="text-[#555555]" /> Contact Info
                    </h3>
                    <div className="p-4 bg-[#050505] border border-[#222222] rounded">
                      <p className="text-xs font-mono text-[#888888] break-all">
                        {selectedLead.contactInfo || 'Not specified'}
                      </p>
                    </div>
                  </section>
                </div>
              </div>
            </div>
          ) : (
            <div className="h-full flex flex-col items-center justify-center text-center p-12">
              <Users size={48} className="text-[#333333] mb-6" />
              <h2 className="text-xl font-mono uppercase tracking-tighter text-[#555555] mb-2">No Lead Selected</h2>
              <p className="text-xs text-[#555555] font-mono uppercase tracking-widest">Select a lead from the sidebar to view details.</p>
            </div>
          )}
        </div>
      </div>

      <AnimatePresence>
        {selectedLeadIds.size > 0 && (
          <motion.div 
            initial={{ y: 100, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: 100, opacity: 0 }}
            className="fixed bottom-8 left-1/2 -translate-x-1/2 z-[150] bg-[#111111] border border-[#222222] rounded px-6 py-4 shadow-2xl flex items-center gap-6"
          >
            <div className="flex items-center gap-3 pr-6 border-r border-[#222222]">
              <div className="w-8 h-8 bg-[#0074D9] rounded flex items-center justify-center text-white font-mono text-xs font-bold">
                {selectedLeadIds.size}
              </div>
              <span className="text-[10px] font-mono uppercase tracking-widest text-[#888888]">Leads Selected</span>
            </div>
            
            <div className="flex items-center gap-2">
              <div className="relative group">
                <button 
                  className="flex items-center gap-2 px-4 py-2 bg-[#222222] hover:bg-[#333333] text-white rounded text-[10px] font-mono uppercase tracking-widest transition-all"
                >
                  Update Status
                </button>
                <div className="absolute bottom-full left-0 mb-2 w-48 bg-[#111111] border border-[#222222] rounded shadow-2xl p-2 space-y-1 opacity-0 group-hover:opacity-100 pointer-events-none group-hover:pointer-events-auto transition-all">
                  {['New', 'Contacted', 'Responded', 'Converted', 'Lost'].map(s => (
                    <button 
                      key={s}
                      onClick={() => handleBulkUpdateStatus(s as any)}
                      className="w-full text-left px-3 py-2 text-[10px] font-mono uppercase text-[#888888] hover:bg-[#222222] hover:text-white rounded transition-colors"
                    >
                      {s}
                    </button>
                  ))}
                </div>
              </div>
              
              <button 
                onClick={handleBulkDeleteLeads}
                className="flex items-center gap-2 px-4 py-2 bg-[#FF4444]/10 hover:bg-[#FF4444]/20 text-[#FF4444] rounded text-[10px] font-mono uppercase tracking-widest transition-all border border-[#FF4444]/20"
              >
                <Trash2 size={14} /> Delete
              </button>
              
              <button 
                onClick={() => setSelectedLeadIds(new Set())}
                className="p-2 text-[#555555] hover:text-white transition-colors"
              >
                <X size={20} />
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Add Lead Modal */}
      <AnimatePresence>
        {isAddingLead && (
          <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm">
            <motion.div 
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              className="bg-[#050505] border border-[#222222] p-8 rounded w-full max-w-lg"
            >
              <div className="flex items-center justify-between mb-8">
                <h2 className="tech-label text-white text-xl">Add New Lead</h2>
                <button onClick={() => setIsAddingLead(false)} className="text-[#555555] hover:text-white">
                  <X size={20} />
                </button>
              </div>
              <form onSubmit={handleAddLead} className="space-y-6 max-h-[70vh] overflow-y-auto pr-2 no-scrollbar">
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <label className="text-[10px] font-mono uppercase text-[#555555]">Name</label>
                    <input 
                      required
                      type="text" 
                      value={newLead.name}
                      onChange={(e) => setNewLead({...newLead, name: e.target.value})}
                      className="tech-input w-full"
                    />
                  </div>
                  <div className="space-y-2">
                    <label className="text-[10px] font-mono uppercase text-[#555555]">Platform</label>
                    <select 
                      value={newLead.platform}
                      onChange={(e) => setNewLead({...newLead, platform: e.target.value as any})}
                      className="tech-input w-full"
                    >
                      <option value="X">X (Twitter)</option>
                      <option value="LinkedIn">LinkedIn</option>
                      <option value="Reddit">Reddit</option>
                      <option value="Email">Email</option>
                    </select>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <label className="text-[10px] font-mono uppercase text-[#555555]">Role</label>
                    <input 
                      type="text" 
                      value={newLead.role}
                      onChange={(e) => setNewLead({...newLead, role: e.target.value})}
                      className="tech-input w-full"
                      placeholder="e.g. CTO"
                    />
                  </div>
                  <div className="space-y-2">
                    <label className="text-[10px] font-mono uppercase text-[#555555]">Company</label>
                    <input 
                      type="text" 
                      value={newLead.company}
                      onChange={(e) => setNewLead({...newLead, company: e.target.value})}
                      className="tech-input w-full"
                      placeholder="e.g. Acme Inc"
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <label className="text-[10px] font-mono uppercase text-[#555555]">Handle / ID</label>
                  <input 
                    type="text" 
                    value={newLead.handle}
                    onChange={(e) => setNewLead({...newLead, handle: e.target.value})}
                    className="tech-input w-full"
                    placeholder="@username or u/username"
                  />
                </div>
                <div className="space-y-2">
                  <label className="text-[10px] font-mono uppercase text-[#555555]">Social Links (JSON style or comma separated)</label>
                  <div className="grid grid-cols-2 gap-2">
                    <input 
                      type="text" 
                      placeholder="X URL"
                      value={newLead.socialLinks?.x}
                      onChange={(e) => setNewLead({...newLead, socialLinks: {...newLead.socialLinks, x: e.target.value}})}
                      className="tech-input"
                    />
                    <input 
                      type="text" 
                      placeholder="LinkedIn URL"
                      value={newLead.socialLinks?.linkedin}
                      onChange={(e) => setNewLead({...newLead, socialLinks: {...newLead.socialLinks, linkedin: e.target.value}})}
                      className="tech-input"
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <label className="text-[10px] font-mono uppercase text-[#555555]">Context / Reason</label>
                  <input 
                    type="text" 
                    value={newLead.context}
                    onChange={(e) => setNewLead({...newLead, context: e.target.value})}
                    className="tech-input w-full"
                    placeholder="Why are they a lead?"
                  />
                </div>
                <div className="space-y-2">
                  <label className="text-[10px] font-mono uppercase text-[#555555]">Details</label>
                  <textarea 
                    value={newLead.details}
                    onChange={(e) => setNewLead({...newLead, details: e.target.value})}
                    className="tech-input w-full h-24 resize-none"
                    placeholder="Additional info..."
                  />
                </div>
                <div className="flex gap-4 pt-4">
                  <button 
                    type="button"
                    onClick={() => setIsAddingLead(false)}
                    className="tech-button-secondary flex-1 py-4"
                  >
                    Cancel
                  </button>
                  <button 
                    type="submit"
                    className="tech-button flex-1 py-4"
                  >
                    Add Lead
                  </button>
                </div>
              </form>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      {/* Edit Lead Modal */}
      <AnimatePresence>
        {isEditingLead && selectedLead && (
          <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm">
            <motion.div 
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              className="bg-[#050505] border border-[#222222] p-8 rounded w-full max-w-lg"
            >
              <div className="flex items-center justify-between mb-8">
                <h2 className="tech-label text-white text-xl">Edit Lead</h2>
                <button onClick={() => setIsEditingLead(false)} className="text-[#555555] hover:text-white">
                  <X size={20} />
                </button>
              </div>
              <div className="space-y-6 max-h-[70vh] overflow-y-auto pr-2 no-scrollbar">
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <label className="text-[10px] font-mono uppercase text-[#555555]">Name</label>
                    <input 
                      type="text" 
                      defaultValue={selectedLead.name}
                      onBlur={(e) => handleUpdateLead({ name: e.target.value })}
                      className="tech-input w-full"
                    />
                  </div>
                  <div className="space-y-2">
                    <label className="text-[10px] font-mono uppercase text-[#555555]">Platform</label>
                    <select 
                      defaultValue={selectedLead.platform}
                      onChange={(e) => handleUpdateLead({ platform: e.target.value as any })}
                      className="tech-input w-full"
                    >
                      <option value="X">X (Twitter)</option>
                      <option value="LinkedIn">LinkedIn</option>
                      <option value="Reddit">Reddit</option>
                      <option value="Email">Email</option>
                    </select>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <label className="text-[10px] font-mono uppercase text-[#555555]">Role</label>
                    <input 
                      type="text" 
                      defaultValue={selectedLead.role}
                      onBlur={(e) => handleUpdateLead({ role: e.target.value })}
                      className="tech-input w-full"
                    />
                  </div>
                  <div className="space-y-2">
                    <label className="text-[10px] font-mono uppercase text-[#555555]">Company</label>
                    <input 
                      type="text" 
                      defaultValue={selectedLead.company}
                      onBlur={(e) => handleUpdateLead({ company: e.target.value })}
                      className="tech-input w-full"
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <label className="text-[10px] font-mono uppercase text-[#555555]">Handle / ID</label>
                  <input 
                    type="text" 
                    defaultValue={selectedLead.handle}
                    onBlur={(e) => handleUpdateLead({ handle: e.target.value })}
                    className="tech-input w-full"
                  />
                </div>
                <div className="space-y-2">
                  <label className="text-[10px] font-mono uppercase text-[#555555]">Context</label>
                  <input 
                    type="text" 
                    defaultValue={selectedLead.context}
                    onBlur={(e) => handleUpdateLead({ context: e.target.value })}
                    className="tech-input w-full"
                  />
                </div>
                <div className="space-y-2">
                  <label className="text-[10px] font-mono uppercase text-[#555555]">Details</label>
                  <textarea 
                    defaultValue={selectedLead.details}
                    onBlur={(e) => handleUpdateLead({ details: e.target.value })}
                    className="tech-input w-full h-24 resize-none"
                  />
                </div>
                <button 
                  onClick={() => setIsEditingLead(false)}
                  className="tech-button w-full py-4"
                >
                  Done
                </button>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </div>
  );
}

function Loader2(props: any) {
  return (
    <svg
      {...props}
      xmlns="http://www.w3.org/2000/svg"
      width="24"
      height="24"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M21 12a9 9 0 1 1-6.219-8.56" />
    </svg>
  );
}
